import { useState } from 'react';
import { X, Calendar, Plus, Trash2, AlertCircle, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface HolidayManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  forecastId: string;
  onSaveHolidays: (holidays: Holiday[]) => void;
}

interface Holiday {
  id?: string;
  date: string;
  name: string;
  type: 'PUBLIC' | 'COMPANY' | 'SPECIAL_EVENT';
  volumeImpact?: 'CLOSED' | 'REDUCED' | 'INCREASED';
  impactPercent?: number;
}

const PRESET_HOLIDAYS_2026 = [
  { date: '2026-01-01', name: "New Year's Day", type: 'PUBLIC' as const },
  { date: '2026-01-19', name: 'Martin Luther King Jr. Day', type: 'PUBLIC' as const },
  { date: '2026-02-16', name: "Presidents' Day", type: 'PUBLIC' as const },
  { date: '2026-05-25', name: 'Memorial Day', type: 'PUBLIC' as const },
  { date: '2026-07-03', name: 'Independence Day (Observed)', type: 'PUBLIC' as const },
  { date: '2026-07-04', name: 'Independence Day', type: 'PUBLIC' as const },
  { date: '2026-09-07', name: 'Labor Day', type: 'PUBLIC' as const },
  { date: '2026-10-12', name: 'Columbus Day', type: 'PUBLIC' as const },
  { date: '2026-11-11', name: 'Veterans Day', type: 'PUBLIC' as const },
  { date: '2026-11-26', name: 'Thanksgiving', type: 'PUBLIC' as const },
  { date: '2026-11-27', name: 'Day After Thanksgiving', type: 'PUBLIC' as const },
  { date: '2026-12-25', name: 'Christmas Day', type: 'PUBLIC' as const },
];

export default function HolidayManagementModal({
  isOpen,
  onClose,
  forecastId: _forecastId,
  onSaveHolidays,
}: HolidayManagementModalProps) {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [newHoliday, setNewHoliday] = useState<Holiday>({
    date: '',
    name: '',
    type: 'PUBLIC',
    volumeImpact: 'CLOSED',
    impactPercent: 0,
  });
  const [showAddForm, setShowAddForm] = useState(false);

  const handleAddHoliday = () => {
    if (!newHoliday.date || !newHoliday.name) {
      alert('Please fill in date and name');
      return;
    }

    setHolidays([...holidays, { ...newHoliday, id: Date.now().toString() }]);
    setNewHoliday({
      date: '',
      name: '',
      type: 'PUBLIC',
      volumeImpact: 'CLOSED',
      impactPercent: 0,
    });
    setShowAddForm(false);
  };

  const handleRemoveHoliday = (id: string) => {
    setHolidays(holidays.filter(h => h.id !== id));
  };

  const handleAddPresetHolidays = () => {
    const newHolidays = PRESET_HOLIDAYS_2026.map(h => ({
      ...h,
      id: Date.now().toString() + Math.random(),
      volumeImpact: 'CLOSED' as const,
      impactPercent: 0,
    }));
    setHolidays([...holidays, ...newHolidays]);
  };

  const handleSave = () => {
    onSaveHolidays(holidays);
    onClose();
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'PUBLIC': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'COMPANY': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'SPECIAL_EVENT': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getImpactColor = (impact?: string) => {
    switch (impact) {
      case 'CLOSED': return 'bg-red-100 text-red-700';
      case 'REDUCED': return 'bg-yellow-100 text-yellow-700';
      case 'INCREASED': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Holiday Management</h2>
                    <p className="text-blue-100 text-sm">
                      Mark holidays and special events to adjust forecasting
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-220px)]">
              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-blue-900">How Holiday Adjustments Work</h3>
                    <p className="text-sm text-blue-700 mt-1">
                      Holidays can be marked as Closed (0% volume), Reduced (partial volume), or Increased (peak demand).
                      The forecast will automatically adjust predictions for these days.
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Add US Holidays Button */}
              <button
                onClick={handleAddPresetHolidays}
                className="w-full mb-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all flex items-center justify-center space-x-2"
              >
                <Plus className="h-5 w-5" />
                <span>Add 2026 US Public Holidays</span>
              </button>

              {/* Add Custom Holiday Button */}
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="w-full mb-6 py-3 border-2 border-dashed border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-blue-500 hover:text-blue-600 transition-all flex items-center justify-center space-x-2"
              >
                <Plus className="h-5 w-5" />
                <span>Add Custom Holiday</span>
              </button>

              {/* Add Holiday Form */}
              {showAddForm && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date
                      </label>
                      <input
                        type="date"
                        value={newHoliday.date}
                        onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Holiday Name
                      </label>
                      <input
                        type="text"
                        value={newHoliday.name}
                        onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                        placeholder="e.g., Company Shutdown"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        value={newHoliday.type}
                        onChange={(e) => setNewHoliday({ ...newHoliday, type: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="PUBLIC">Public Holiday</option>
                        <option value="COMPANY">Company Holiday</option>
                        <option value="SPECIAL_EVENT">Special Event</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Volume Impact
                      </label>
                      <select
                        value={newHoliday.volumeImpact}
                        onChange={(e) => setNewHoliday({ ...newHoliday, volumeImpact: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="CLOSED">Closed (0%)</option>
                        <option value="REDUCED">Reduced</option>
                        <option value="INCREASED">Increased</option>
                      </select>
                    </div>
                    {newHoliday.volumeImpact !== 'CLOSED' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Impact %
                        </label>
                        <input
                          type="number"
                          min="-100"
                          max="500"
                          value={newHoliday.impactPercent}
                          onChange={(e) => setNewHoliday({ ...newHoliday, impactPercent: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={() => setShowAddForm(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddHoliday}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                    >
                      Add Holiday
                    </button>
                  </div>
                </div>
              )}

              {/* Holidays List */}
              {holidays.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-1">No Holidays Added</h3>
                  <p className="text-sm text-gray-600">
                    Add US public holidays or create custom holidays
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">
                      Marked Holidays ({holidays.length})
                    </h3>
                  </div>

                  {/* Sort holidays by date */}
                  {holidays
                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                    .map((holiday) => (
                      <div
                        key={holiday.id}
                        className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 flex-1">
                            <div className="text-center">
                              <div className="text-2xl font-bold text-gray-900">
                                {new Date(holiday.date).getDate()}
                              </div>
                              <div className="text-xs text-gray-600">
                                {new Date(holiday.date).toLocaleDateString('en-US', { month: 'short' })}
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(holiday.date).toLocaleDateString('en-US', { year: 'numeric' })}
                              </div>
                            </div>

                            <div className="flex-1">
                              <div className="font-semibold text-gray-900 mb-1">
                                {holiday.name}
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className={`px-2 py-1 rounded text-xs font-medium border ${getTypeColor(holiday.type)}`}>
                                  {holiday.type.replace('_', ' ')}
                                </span>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${getImpactColor(holiday.volumeImpact)}`}>
                                  {holiday.volumeImpact === 'CLOSED'
                                    ? 'Closed'
                                    : holiday.volumeImpact === 'REDUCED'
                                    ? `Reduced ${holiday.impactPercent}%`
                                    : `Increased ${holiday.impactPercent}%`}
                                </span>
                              </div>
                            </div>
                          </div>

                          <button
                            onClick={() => holiday.id && handleRemoveHoliday(holiday.id)}
                            className="text-red-600 hover:text-red-700 transition-colors p-2"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                {holidays.length} {holidays.length === 1 ? 'holiday' : 'holidays'} marked
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  <span>Save Holidays</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
